import type { Locale } from "date-fns";

import { dateFnsLocaleFor } from "./date-fns-locales";
import {
  activeLanguage,
  formatDate,
  formatDateTime,
  formatNumber,
  formatTime,
  type DateFormat,
  type DateInput,
} from "./format";

/**
 * Formatting for code that cannot hold a hook.
 *
 * `useFormatters()` is the entry point inside a component — it binds the
 * language AND the workspace currency, and its identity changes on a language
 * switch. But the biggest formatting surface in this app is not components: it
 * is the row mappers (`transformDealForDisplay` and friends), which are plain
 * module-scope functions called once per grid row and cannot call a hook.
 *
 * Those call these instead. `activeLanguage()` reads the live i18next singleton
 * at CALL time, never at import time, so the value a mapper produces follows the
 * current language; the component that owns the mapper re-renders on a switch
 * because it subscribes through `useTranslation()`, and the mapper then re-runs.
 *
 * Same scope rule as `format.ts`: DISPLAY ONLY. A date that is written back
 * through a mutation, parsed again, or used as a sort key is a VALUE, and must
 * keep its stable machine shape — a German locale renders "22.09.2026" and the
 * parser on the other end will not thank you.
 */

/** Localized date. `style` defaults to medium — `Sep 17, 2026` in en-US. */
export function displayDate(date: DateInput, style?: DateFormat): string {
  return formatDate(date, activeLanguage(), style);
}

/** Localized date plus short clock time. */
export function displayDateTime(date: DateInput, style?: DateFormat): string {
  return formatDateTime(date, activeLanguage(), style);
}

/** Localized clock time — `3:04 PM` in en-US, `15:04` in de-DE. */
export function displayTime(date: DateInput): string {
  return formatTime(date, activeLanguage());
}

/** Localized number — grouping and decimal marks follow the reader's language. */
export function displayNumber(
  value: number | null | undefined,
  options?: Intl.NumberFormatOptions,
): string {
  return formatNumber(value, activeLanguage(), options);
}

/**
 * The date-fns locale for the live language, for the calls Intl cannot replace:
 * `formatDistanceToNow` and the calendar components. Pass it as the `locale`
 * option — without one, date-fns writes English month and weekday names into
 * every other language.
 */
export function displayLocale(): Locale {
  return dateFnsLocaleFor(activeLanguage());
}

/**
 * Date shapes `dateStyle` cannot express, named once so the call sites stay
 * readable and every options object is a stable module constant (the Intl
 * instance cache in `format.ts` keys on the option VALUES, but a shared
 * constant also keeps these out of React dependency arrays).
 *
 * Each is the exact Intl equivalent of the date-fns token pattern it replaced,
 * so English output is unchanged: `MMM d`, `EEE, MMM d`, `EEEE, MMMM d`.
 */
export const MONTH_DAY: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };

export const WEEKDAY_MONTH_DAY: Intl.DateTimeFormatOptions = {
  weekday: "short",
  month: "short",
  day: "numeric",
};

/** Day and full month, no year — `September 17` in en-US. */
export const MONTH_DAY_LONG: Intl.DateTimeFormatOptions = { month: "long", day: "numeric" };

/** Just the month name, for a month picker's option labels. */
export const MONTH_NAME: Intl.DateTimeFormatOptions = { month: "long" };

export const WEEKDAY_LONG: Intl.DateTimeFormatOptions = {
  weekday: "long",
  month: "long",
  day: "numeric",
};
